import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CardBody } from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { getStandards } from "../../services/reference";
import "./Report.css";
import "../controls/style.css";
import { getInstitutions } from "../../services/institutions";
import { getReport as getReportApi } from "src/services/reference";
import i18n from "../../i18n";
import * as XLSX from "xlsx";
import { FileSpreadsheet, FilterIcon } from "lucide-react";
import LoadingScreen from "src/Loading/LoadingScreen";
import { useSearchQuery } from "src/hook/useSearchQuery";
import { FiSearch } from "react-icons/fi";

interface StandardsData {
  id?: string;
  code: string;
  name: string;
  domains: Domain[];
}

interface Domain {
  id: string;
  code: string;
  en_name: string;
  ar_name: string;
  parentId: string | null;
  subdomains: Subdomain[];
}

interface Subdomain {
  id: string;
  code: string;
  en_name: string;
  ar_name: string;
}

export type ReportFilters = {
  standard: string | null;
  domain: string | null;
  subdomain: string | null;
  institution: string | null;
  status: string | null;
  date: string | null;
};

const emptyFilters = (): ReportFilters => ({
  standard: null,
  domain: null,
  subdomain: null,
  institution: null,
  status: null,
  date: null,
});

const ReportSupCompliance: React.FC = () => {
  const { t } = useTranslation();
  const tr = (key: string) => String(t(`Reports.${key}`));

  const [selectedStandard, setSelectedStandard] = useState<string | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [selectedSubDomain, setSelectedSubDomain] = useState<string | null>(null);
  const [domainOptions, setDomainOptions] = useState<{ value: string; label: string }[]>([]);
  const [subDomainOptions, setSubDomainOptions] = useState<{ value: string; label: string }[]>([]);
  const [data, setData] = useState<StandardsData[]>([]);
  const [institutions, setInstitutions] = useState<{ id: string; name: string }[]>([]);
  const [selectedInstitution, setSelectedInstitution] = useState<string | null>(null);
  const [reports, setReports] = useState<Record<string, unknown>[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [reportsLoading, setReportsLoading] = useState<boolean>(false);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [draftFilters, setDraftFilters] = useState<ReportFilters>(emptyFilters());
  const [tableSearch, setTableSearch] = useState("");
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");

  const { searchQuery, searchResultType, clearSearchQuery } = useSearchQuery();

  const selectMenuPortal = typeof document !== "undefined" ? document.body : undefined;

  const customSelectStyles = useMemo(
    () => ({
      control: (provided: Record<string, unknown>) => ({
        ...provided,
        minHeight: "42px",
        borderRadius: "10px",
        borderColor: "#e2e8f0",
        boxShadow: "none",
      }),
      valueContainer: (provided: Record<string, unknown>) => ({
        ...provided,
        minHeight: "42px",
        padding: "0 10px",
      }),
      input: (provided: Record<string, unknown>) => ({
        ...provided,
        margin: "0px",
      }),
      indicatorsContainer: (provided: Record<string, unknown>) => ({
        ...provided,
        height: "42px",
      }),
      menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 12000 }),
    }),
    []
  );

  const fetchReport = useCallback(async (f: ReportFilters) => {
    setReportsLoading(true);
    try {
      const response: { success?: boolean; data?: Record<string, unknown>[] } = await getReportApi(
        f.standard,
        f.subdomain,
        f.domain,
        f.institution,
        f.status,
        f.date
      );
      if (response.success) {
        setReports(response.data || []);
      } else {
        toast.error("Error fetching report");
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Error fetching report");
    } finally {
      setReportsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [stdRes, instRes] = await Promise.all([getStandards(), getInstitutions("", 1000, 0)]);
        if (cancelled) return;
        if (stdRes?.success) setData(stdRes.data || []);
        else {
          console.error("Unexpected standards response:", stdRes);
          toast.error(tr("fetch_standards_error"));
        }
        if (instRes?.success) setInstitutions(instRes.data || []);
        await fetchReport(emptyFilters());
      } catch (e: unknown) {
        console.error(e);
        toast.error(tr("network_error"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchReport]);

  useEffect(() => {
    if (searchQuery && searchResultType === "reports") {
      clearSearchQuery();
    }
  }, [searchQuery, searchResultType, clearSearchQuery]);

  useEffect(() => {
    if (selectedStandard) {
      const domains =
        data.find((s) => s.id === selectedStandard)?.domains || [];
      setDomainOptions(
        domains
          .filter((d) => d.parentId === null || d.parentId === undefined)
          .map((d) => ({
            value: d.id,
            label: i18n.language === "ar" ? d.ar_name : d.en_name,
          }))
      );
      setSelectedDomain(null);
      setSubDomainOptions([]);
    } else {
      setDomainOptions([]);
      setSelectedDomain(null);
      setSubDomainOptions([]);
    }
  }, [selectedStandard, data]);

  useEffect(() => {
    if (selectedDomain) {
      const selectedDomainObj = data.flatMap((s) => s.domains || []).find((d) => d.id === selectedDomain);
      const subdomains = selectedDomainObj?.subdomains || [];
      setSubDomainOptions(
        subdomains.map((sd) => ({
          value: sd.id,
          label: i18n.language === "ar" ? sd.ar_name : sd.en_name,
        }))
      );
      setSelectedSubDomain(null);
    } else {
      setSubDomainOptions([]);
      setSelectedSubDomain(null);
    }
  }, [selectedDomain, data]);

  const syncDomainOptionsForDraft = useCallback(
    (standardId: string | null) => {
      if (!standardId) return [];
      const domains = data.find((s) => s.id === standardId)?.domains || [];
      return domains
        .filter((d) => d.parentId === null || d.parentId === undefined)
        .map((d) => ({
          value: d.id,
          label: i18n.language === "ar" ? d.ar_name : d.en_name,
        }));
    },
    [data]
  );

  const syncSubDomainOptionsForDraft = useCallback(
    (domainId: string | null) => {
      if (!domainId) return [];
      const selectedDomainObj = data.flatMap((s) => s.domains || []).find((d) => d.id === domainId);
      const subdomains = selectedDomainObj?.subdomains || [];
      return subdomains.map((sd) => ({
        value: sd.id,
        label: i18n.language === "ar" ? sd.ar_name : sd.en_name,
      }));
    },
    [data]
  );

  const [draftDomainOptions, setDraftDomainOptions] = useState<{ value: string; label: string }[]>([]);
  const [draftSubDomainOptions, setDraftSubDomainOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    if (!filterPopoverOpen) return;
    setDraftDomainOptions(syncDomainOptionsForDraft(draftFilters.standard));
    setDraftSubDomainOptions(syncSubDomainOptionsForDraft(draftFilters.domain));
  }, [filterPopoverOpen, draftFilters.standard, draftFilters.domain, syncDomainOptionsForDraft, syncSubDomainOptionsForDraft]);

  const openFilterPopover = () => {
    setDraftFilters({
      standard: selectedStandard,
      domain: selectedDomain,
      subdomain: selectedSubDomain,
      institution: selectedInstitution,
      status: selectedStatus,
      date: selectedDate,
    });
    setDraftDomainOptions(syncDomainOptionsForDraft(selectedStandard));
    setDraftSubDomainOptions(syncSubDomainOptionsForDraft(selectedDomain));
    setFilterPopoverOpen(true);
  };

  /** Funnel toggles the anchored panel (same pattern as catalog filter). × and Apply also close when needed. */
  const onFilterButtonClick = () => {
    if (filterPopoverOpen) closeFilterPopover();
    else openFilterPopover();
  };

  const closeFilterPopover = () => setFilterPopoverOpen(false);

  /** Revert draft fields to last applied filters; panel stays open. */
  const resetDraftToApplied = () => {
    setDraftFilters({
      standard: selectedStandard,
      domain: selectedDomain,
      subdomain: selectedSubDomain,
      institution: selectedInstitution,
      status: selectedStatus,
      date: selectedDate,
    });
    setDraftDomainOptions(syncDomainOptionsForDraft(selectedStandard));
    setDraftSubDomainOptions(syncSubDomainOptionsForDraft(selectedDomain));
  };

  const applyFiltersFromPopover = async () => {
    setSelectedStandard(draftFilters.standard);
    setSelectedDomain(draftFilters.domain);
    setSelectedSubDomain(draftFilters.subdomain);
    setSelectedInstitution(draftFilters.institution);
    setSelectedStatus(draftFilters.status);
    setSelectedDate(draftFilters.date);
    await fetchReport(draftFilters);
    setFilterPopoverOpen(false);
  };

  const clearDraftFilters = () => {
    setDraftFilters(emptyFilters());
    setDraftDomainOptions([]);
    setDraftSubDomainOptions([]);
  };

  const displayRows = useMemo(() => {
    let rows = [...reports];
    const q = tableSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const cells = [
          r.institution,
          r.standard,
          r.domain,
          r.sub_domain,
          r.control,
          r.compliance_status,
          r.status,
        ]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase());
        return cells.some((c) => c.includes(q));
      });
    }
    rows.sort((a, b) => {
      const ta = new Date(String((a as { updatedAt?: string }).updatedAt || 0)).getTime();
      const tb = new Date(String((b as { updatedAt?: string }).updatedAt || 0)).getTime();
      return sortOrder === "newest" ? tb - ta : ta - tb;
    });
    return rows;
  }, [reports, tableSearch, sortOrder]);

  const exportToExcel = () => {
    const rows = displayRows.map((report) => ({
      ...report,
      standard: Array.isArray(report.standard)
        ? (report.standard as unknown[]).join(",")
        : ((report.standard as string) ?? ""),
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");
    XLSX.writeFile(workbook, "reports.xlsx");
  };

  if (loading) {
    return (
      <div className="loading-screen" style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}>
        <div>
          <LoadingScreen />
        </div>
      </div>
    );
  }

  const hasAppliedReportFilters = Boolean(
    selectedStandard ||
      selectedDomain ||
      selectedSubDomain ||
      selectedInstitution ||
      selectedStatus ||
      selectedDate,
  );

  const statusOptions = [
    { value: "Fully Implemented", label: t("view_control.Fully-Implemented") },
    { value: "Implemented", label: t("view_control.Implemented") },
    { value: "Mostly Implemented", label: t("view_control.Mostly-Implemented") },
    { value: "Partially Implemented", label: t("view_control.Partially-Implemented") },
    { value: "Averagely Implemented", label: t("view_control.Averagely-Implemented") },
    { value: "Not Implemented", label: t("view_control.Not-Implemented") },
  ];

  return (
    <div className="root report-sup-page">
     

      <div
        className={`report-sup-control-card
          ${filterPopoverOpen ? " report-sup-control-card--filter-open" : ""}`}
      >
        <div className="report-sup-toolbar">
          <div className="report-sup-search-wrap">
            <FiSearch className="report-sup-search-icon" size={18} aria-hidden />
            <input
              type="search"
              className="report-sup-search-input"
              placeholder={tr("search_placeholder")}
              aria-label={tr("search_placeholder")}
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
            />
          </div>
          <div className="report-sup-toolbar-actions">
            <div className={`report-sup-filter-anchor${filterPopoverOpen ? " report-sup-filter-anchor--open" : ""}`}>
              <div
                className={`controls-catalog-tab-pair${filterPopoverOpen ? " controls-catalog-tab-pair--open" : ""}${
                  hasAppliedReportFilters ? " controls-catalog-tab-pair--has-filters" : ""
                }`}
                role="group"
                aria-label={String(t("controls.filter") ?? "Filter")}
              >
                <span className="controls-catalog-tab-pair__first">
                  {String(t("controls.catalogViewTab", { defaultValue: "List" }))}
                </span>
                <button
                  ref={filterBtnRef}
                  type="button"
                  className="controls-catalog-tab-pair__filter"
                  onClick={onFilterButtonClick}
                  aria-label={String(t("controls.filter"))}
                  aria-pressed={filterPopoverOpen}
                  aria-expanded={filterPopoverOpen}
                  aria-haspopup="dialog"
                  title={tr("open_filters")}
                >
                  <FilterIcon size={18} aria-hidden />
                </button>
              </div>
              {filterPopoverOpen ? (
                <div
                  ref={popoverRef}
                  className="report-sup-filter-popover report-sup-filter-popover--anchored"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="report-filter-popover-title"
                >
                  <div className="report-sup-filter-popover-header">
                    <div>
                      <div id="report-filter-popover-title" className="report-sup-filter-popover-title">
                        {tr("filter_title")}
                      </div>
                      <p className="report-sup-filter-popover-hint">{tr("filter_subtitle")}</p>
                    </div>
                    <button
                      type="button"
                      className="report-sup-filter-popover-close"
                      onClick={closeFilterPopover}
                      aria-label={String(tr("close_panel"))}
                    >
                      ×
                    </button>
                  </div>
                  <div className="report-sup-filter-popover-body">
                    <div className="report-sup-filter-grid">
                      <div className="report-sup-field">
                        <label className="report-sup-field-label">{tr("Select Standard")}</label>
                        <Select
                          options={data.map((s) => ({ value: String(s.id ?? s.code), label: s.name }))}
                          value={
                            data
                              .map((s) => ({ value: String(s.id ?? s.code), label: s.name }))
                              .find((o) => o.value === draftFilters.standard) || null
                          }
                          onChange={(option) => {
                            const v = (option as { value: string } | null)?.value || null;
                            setDraftFilters((prev) => ({
                              ...prev,
                              standard: v,
                              domain: null,
                              subdomain: null,
                            }));
                            setDraftDomainOptions(syncDomainOptionsForDraft(v));
                            setDraftSubDomainOptions([]);
                          }}
                          placeholder={tr("Select Standard")}
                          styles={customSelectStyles}
                          menuPortalTarget={selectMenuPortal}
                          menuPosition="fixed"
                          isClearable
                        />
                      </div>
                      <div className="report-sup-field">
                        <label className="report-sup-field-label">{tr("Select Domain")}</label>
                        <Select
                          options={draftDomainOptions}
                          value={draftDomainOptions.find((o) => o.value === draftFilters.domain) || null}
                          onChange={(option) => {
                            const v = (option as { value: string } | null)?.value || null;
                            setDraftFilters((prev) => ({
                              ...prev,
                              domain: v,
                              subdomain: null,
                            }));
                            setDraftSubDomainOptions(syncSubDomainOptionsForDraft(v));
                          }}
                          placeholder={tr("Select Domain")}
                          isDisabled={!draftFilters.standard}
                          styles={customSelectStyles}
                          menuPortalTarget={selectMenuPortal}
                          menuPosition="fixed"
                          isClearable
                        />
                      </div>
                      <div className="report-sup-field">
                        <label className="report-sup-field-label">{tr("Select Sub-domain")}</label>
                        <Select
                          options={draftSubDomainOptions}
                          value={draftSubDomainOptions.find((o) => o.value === draftFilters.subdomain) || null}
                          onChange={(option) =>
                            setDraftFilters((prev) => ({
                              ...prev,
                              subdomain: (option as { value: string } | null)?.value || null,
                            }))
                          }
                          placeholder={tr("Select Sub-domain")}
                          isDisabled={!draftFilters.domain}
                          styles={customSelectStyles}
                          menuPortalTarget={selectMenuPortal}
                          menuPosition="fixed"
                          isClearable
                        />
                      </div>
                      <div className="report-sup-field">
                        <label className="report-sup-field-label">{tr("Select Institution")}</label>
                        <Select
                          options={institutions.map((i) => ({ value: i.id, label: i.name }))}
                          value={
                            institutions
                              .map((i) => ({ value: i.id, label: i.name }))
                              .find((o) => o.value === draftFilters.institution) || null
                          }
                          onChange={(option) =>
                            setDraftFilters((prev) => ({
                              ...prev,
                              institution: (option as { value: string } | null)?.value || null,
                            }))
                          }
                          placeholder={tr("Select Institution")}
                          styles={customSelectStyles}
                          menuPortalTarget={selectMenuPortal}
                          menuPosition="fixed"
                          isClearable
                        />
                      </div>
                      <div className="report-sup-field">
                        <label className="report-sup-field-label">{tr("Select Compliance Status")}</label>
                        <Select
                          options={statusOptions}
                          value={statusOptions.find((o) => o.value === draftFilters.status) || null}
                          onChange={(option) =>
                            setDraftFilters((prev) => ({
                              ...prev,
                              status: (option as { value: string } | null)?.value || null,
                            }))
                          }
                          placeholder={tr("Select Compliance Status")}
                          styles={customSelectStyles}
                          menuPortalTarget={selectMenuPortal}
                          menuPosition="fixed"
                          isClearable
                        />
                      </div>
                      <div className="report-sup-field">
                        <label className="report-sup-field-label">{tr("Select Date")}</label>
                        <input
                          type="date"
                          className="report-sup-date-input"
                          value={draftFilters.date || ""}
                          onChange={(e) =>
                            setDraftFilters((prev) => ({
                              ...prev,
                              date: e.target.value || null,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="report-sup-filter-popover-footer">
                    <button type="button" className="report-sup-btn-muted" onClick={resetDraftToApplied}>
                      {tr("reset_draft")}
                    </button>
                    <button type="button" className="report-sup-btn-muted" onClick={clearDraftFilters}>
                      {tr("clear")}
                    </button>
                    <button
                      type="button"
                      className="report-sup-btn-primary report-sup-btn-apply"
                      onClick={() => void applyFiltersFromPopover()}
                    >
                      {tr("apply")}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="report-sup-excel-btn"
              onClick={() => exportToExcel()}
              disabled={displayRows.length === 0}
              aria-label={tr("export_excel")}
              title={tr("export_excel")}
            >
              <FileSpreadsheet size={20} strokeWidth={2} aria-hidden className="report-sup-excel-btn__icon" />
            </button>
          </div>
        </div>
      </div>

      <div className="report-sup-meta-bar">
        <span className="report-sup-total">
          {tr("total_records")}: <strong>{displayRows.length}</strong>
        </span>
        <div className="report-sup-sort-wrap">
          <label className="report-sup-sort-label" htmlFor="report-sort-order">
            {tr("sort_label")}
          </label>
          <select
            id="report-sort-order"
            className="report-sup-sort-select"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as "newest" | "oldest")}
          >
            <option value="newest">{tr("sort_newest")}</option>
            <option value="oldest">{tr("sort_oldest")}</option>
          </select>
        </div>
      </div>

      <div className="report-sup-table-panel">
        <CardBody className="report-sup-card-body">
          <div className="table-responsive position-relative">
            {reportsLoading ? (
              <div className="report-sup-table-overlay">
                <div className="spinner-border report-sup-spinner" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : null}
            <table className="report-sup-table">
              <thead>
                <tr>
                  <th>{tr("Institution Name")}</th>
                  <th>{tr("Standard")}</th>
                  <th>{tr("Domain")}</th>
                  <th>{tr("Function")}</th>
                  <th>{tr("Control")}</th>
                  <th>{t("view_control.compliance_status")}</th>
                  <th>{tr("Status")}</th>
                  <th>{tr("last update")}</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="report-sup-empty-cell">
                      {tr("No data found")}
                    </td>
                  </tr>
                ) : (
                  displayRows.map((item, index) => (
                    <tr key={String((item as { id?: string }).id ?? index)}>
                      <td>{String((item as { institution?: string }).institution ?? "")}</td>
                      <td>{String((item as { standard?: unknown }).standard ?? "")}</td>
                      <td>{String((item as { domain?: string }).domain ?? "")}</td>
                      <td>{String((item as { sub_domain?: string }).sub_domain ?? "")}</td>
                      <td>{String((item as { control?: string }).control ?? "")}</td>
                      <td>{String((item as { compliance_status?: string }).compliance_status ?? "")}</td>
                      <td>
                        <span
                          className={`report-sup-badge ${
                            (item as { status?: string }).status === "Done"
                              ? "report-sup-badge--success"
                              : (item as { status?: string }).status === "In Progress"
                                ? "report-sup-badge--warning"
                                : "report-sup-badge--muted"
                          }`}
                        >
                          {String((item as { status?: string | null }).status ?? "—")}
                        </span>
                      </td>
                      <td>
                        {(() => {
                          const raw = (item as { updatedAt?: string }).updatedAt;
                          if (!raw) return "—";
                          try {
                            return new Date(raw).toLocaleDateString();
                          } catch {
                            return String(raw);
                          }
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </div>

    </div>
  );
};

export default ReportSupCompliance;
